import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
} from '@plato/ui/containers/dialog';
import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import type { RouterOutputs } from '../../../utils/api.ts';
import { Catalog } from './catalog.tsx';

type CatalogDialogProps = {
    baseUrl: string;
    lastUrl: string;
    setLastUrl: (url: string) => void;
    onAddLineItem: (product: ProductVariantDTO, quantity: number) => void;
};

// The actual dialog component with iframe
const CatalogDialog = DialogStateManager.create(
    ({ baseUrl, lastUrl, onAddLineItem, setLastUrl }: CatalogDialogProps) => {
        const { visible, hide } = useDialog();
        const { t } = useTranslation('catalog');

        return (
            <Dialog
                open={visible}
                onClose={() => {
                    void hide();
                }}
                disablePointerDismissal
            >
                <DialogContent snapMode="fullscreen" className="sm:h-full sm:max-w-[calc(100vw-4rem)]">
                    <DialogHeader>
                        <DialogTitle>{t('iframe_title')}</DialogTitle>
                        <DialogIconClose />
                    </DialogHeader>
                    <DialogBody className="p-0">
                        <Catalog
                            baseUrl={baseUrl}
                            lastUrl={lastUrl}
                            setLastUrl={setLastUrl}
                            onAddLineItem={onAddLineItem}
                        />
                    </DialogBody>
                </DialogContent>
            </Dialog>
        );
    },
);

type CatalogContextType = {
    showCatalog: (
        onAddLineItem: (product: ProductVariantDTO, quantity: number) => void,
        draftId?: string,
    ) => Promise<void>;
};

const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

type CatalogProviderProps = {
    user: NonNullable<RouterOutputs['platoUser']['me']['getMe']>;
    children: ReactNode;
};

export function CatalogProvider({ user, children }: CatalogProviderProps) {
    const webshopUrl = user.platoCustomer.webshopUrl || '';
    const [lastUrl, setLastUrl] = useState<string | null>(null);

    const showCatalog = async (
        onAddLineItem: (product: ProductVariantDTO, quantity: number) => void,
    ): Promise<void> => {
        await DialogStateManager.show(CatalogDialog, {
            baseUrl: webshopUrl,
            lastUrl: lastUrl || webshopUrl,
            setLastUrl,
            onAddLineItem,
        });
    };

    return <CatalogContext.Provider value={{ showCatalog }}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
    const context = useContext(CatalogContext);
    if (!context) {
        throw new Error('useCatalog must be used within a CatalogProvider');
    }
    return context;
}
